import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ScrollText, LogIn, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--paper)] px-5 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/logo.png" alt="Pamaverliefd logo" className="h-12 w-12 rounded-xl object-cover shadow-sm" />
          <div>
            <h1 className="font-serif text-3xl font-bold text-[var(--ink)]">Pamaverliefd</h1>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-3)]">AI Transcriptie</p>
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-6 shadow-sm" data-testid="login-form">
          <h2 className="mb-1 font-serif text-xl font-semibold text-[var(--ink)]">Inloggen</h2>
          <p className="mb-5 text-sm text-[var(--ink-3)]">Log in met je goedgekeurde account.</p>

          {error && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
              {error}
            </div>
          )}

          <label className="mb-3 block">
            <span className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">E-mail</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="h-11 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] px-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
            />
          </label>
          <label className="mb-5 block">
            <span className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Wachtwoord</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="h-11 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] px-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--brand)] text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            Inloggen
          </button>
          <p className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-[var(--ink-3)] hover:text-[var(--brand)] hover:underline" data-testid="login-forgot-link">
              Wachtwoord vergeten?
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-[var(--ink-3)]">
            Nog geen account?{" "}
            <Link to="/register" className="font-semibold text-[var(--brand)] hover:underline" data-testid="login-to-register">
              Aanmelden
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
